import {
    CoreApi,
    CoreApiProvider,
    Dropdown,
    LandingPageContainer,
    LandingPageDataDTO,
    LoadingIndicator,
    Select,
    SingleFilterDropDown,
    Thumbnail,
    ToggleButtons,
} from '@ygtechnologies/bygr-frontend-component-library';
import React, { useState } from 'react';
import { getUserFileImage } from 'services/files';
import { getConstructionPhases, getLandingPageData } from 'services/projects';
import { BygrProjectDetails } from 'types';
import { useAsyncState } from 'utils/hooks';
import theme from './LandingPage.module.scss';

interface ProjectLandingPageProps {
    project: BygrProjectDetails;
}
export interface ConstructionPhase {
    id: number;
    projectId: number;
    name: string;
}
const ProjectLandingPage: React.FC<ProjectLandingPageProps> = ({ project }) => {
    const [data] = useAsyncState(() => getLandingPageData(project.id), [project.id], null);
    const [constructionPhases] = useAsyncState(() => getConstructionPhases(project.id), [project.id], null);
    const [activeConstructionPhase, setActiveConstructionPhase] = useState<number | null>(null);
    const [activeModule, setActiveModule] = useState('cm');

    if (data === null) {
        return <LoadingIndicator />;
    }

    const renderCmPart = () => {
        if (data?.cmImages.filter((x) => x.constructionPhaseId === activeConstructionPhase).length === 0) {
            return <span>No Cm landingPage images</span>;
        }
        const imagesToShow = data?.cmImages.filter((x) => x.constructionPhaseId === activeConstructionPhase).slice(0, 3); // orderBy(buyerData.images, 'sortOrder', 'asc');
        const hasTwoImages = imagesToShow.length === 2;
        return (
            <div className={theme.imageSection}>
                <div className={theme.imageGrid}>
                    <Thumbnail
                        style={{ gridRow: 'span 2', gridColumn: hasTwoImages ? 'span 2' : 'span 3' }}
                        className={theme.mainImage}
                        src={imagesToShow[0] ? getUserFileImage(imagesToShow[0].userFileId!) : ''}
                    />
                    <>
                        {imagesToShow.slice(1, 3).map((i, index) => (
                            <Thumbnail
                                style={hasTwoImages ? { gridRow: 'span 2' } : undefined}
                                key={i.id}
                                src={i ? getUserFileImage(i.userFileId!) : ''}
                            />
                        ))}
                    </>
                </div>
            </div>
        );
    };
    const renderAmPart = () => {
        if (data?.amImages.filter((x) => x.constructionPhaseId === activeConstructionPhase).length === 0) {
            return <span>No Am landingPage images</span>;
        }
        const imagesToShow = data?.amImages.filter((x) => x.constructionPhaseId === activeConstructionPhase).slice(0, 4); // orderBy(buyerData.images, 'sortOrder', 'asc');
        return (
            <div className={theme.imageSection}>
                <div className={theme.imageGrid}>
                    <Thumbnail
                        style={{ gridRow: 'span 2', gridColumn: 'span 3' }}
                        className={theme.mainImage}
                        src={imagesToShow[0] ? getUserFileImage(imagesToShow[0].userFileId!) : ''}
                    />
                </div>
            </div>
        );
    };

    if (!constructionPhases) return <LoadingIndicator />;
    const options = [{ value: null, label: `Default` }, ...constructionPhases.map((x) => ({ value: x.id, label: x.name }))];

    const landingPageData: LandingPageDataDTO = {
        images:
            activeModule === 'cm'
                ? data.cmImages
                      .filter((x) => x.userFileId && x.constructionPhaseId === activeConstructionPhase)
                      .map((x) => ({ ...x, id: x.userFileId! }))
                : data.amImages
                      .filter((x) => x.userFileId && x.constructionPhaseId === activeConstructionPhase)
                      .map((x) => ({ ...x, id: x.userFileId! })),
        contacts: [],
        details: [],
        floorPlans: [],
        messages: [],
        milestones: [],
        projectId: project.id,
        settings: [],
        showBackgroundImage: false,
    };

    const apiHack = new CoreApi();
    apiHack.landingPage.getImageUrl = (imageId: number) => {
        return getUserFileImage(imageId);
    };

    return (
        <CoreApiProvider projectId={project.id} api={apiHack} profileId={project.sellerProfile.id}>
            <div className={theme.landingPageContainer}>
                <LandingPageContainer data={landingPageData}>
                    <div className={theme.filterContainer}>
                        <Select
                            onChange={(v: any) => setActiveConstructionPhase(v.value ?? null)}
                            placeholder={`Select construction phase`}
                            options={options}
                            value={options.find((x) => x.value === activeConstructionPhase)}
                        />
                        <ToggleButtons>
                            <ToggleButtons.Button onClick={() => setActiveModule('cm')} isActive={activeModule === 'cm'}>
                                CM
                            </ToggleButtons.Button>
                            <ToggleButtons.Button onClick={() => setActiveModule('am')} isActive={activeModule === 'am'}>
                                Am
                            </ToggleButtons.Button>
                        </ToggleButtons>
                    </div>
                </LandingPageContainer>
            </div>
        </CoreApiProvider>
    );
};

export default ProjectLandingPage;
